export interface PlanLevelGrantTemplate {
  templateId: string;
  planName: string;
  planLevelGrantTemplate: string;
}

export interface ListOfPlansWithoutTemplate {
  templateId: string;
  planName: string;
  planLevelGrantTemplate?: string;
}

export interface GrantLetterTemplate {
  templateId?: string;
  planName: string;
  templateName?: string;
  esopPlanId?: string;
  s3Path?: string;
  entityType?: string;
  isPrivate: boolean;
  isChanged: boolean;
  isDeleted: boolean;
  isAdded: boolean;
  planLevelGrantTemplate?: string;
  grantLevelTemplate?: string;
  isPlatFormDefault?: boolean;
}

export interface NomineeFormTemplate {
  templateId?: string;
  s3Path?: string;
  entityType?: string;
  fileName?: string;
  fileBlob?: string;
  fileType?: string;
  isChanged: boolean;
  isDeleted: boolean;
  isAdded: boolean;
  isDefault?: boolean;
}
export interface SiteSettingsRes {
  showPPSToEmployees?: boolean;
  emailsToBeSent: boolean;
  approverMailToAdmin: boolean;
  faqForEmployee?: boolean;
  communicationEmailId?: string;
  grantSignature?: string;
  planLevelGrantTemplate: PlanLevelGrantTemplate[];
  listOfPlansWithoutTemplates: ListOfPlansWithoutTemplate[];
  grantLetterTemplates: GrantLetterTemplate[];
  ppsForEmployee?: number;
  acceptanceNumber?: number;
  senderNameForEmail?: string;
  isEsignEnabled: boolean;
  showEmpPortalValuesForecast: boolean;
  overrideGrantLetter: boolean;
  isAttachmentEnabled: boolean;
  isEnabledTwoFactorAuthentication: boolean;
  reminderEmailsToBeSent: boolean;
  reminderEmailsStartFrom?: number;
  reminderEmailsInterval?: number;
  employeeInactivityLogout: boolean;
  shouldEmployeeBeIntimidated: boolean;
  isDocuSignEnabled: boolean;
  isDigioEnabled: boolean;
  signerDetails: SignerDetails[];
  signerDetailsDtos?: SignerDetails[];
  showPlanToEmployees?: boolean;
  sendEmailsToEmployees: boolean;
  sendGrantEmailsToEmployees: boolean;
  sendVestingEmailsToEmployees: boolean;
  sendBuyBackEmailsToEmployees: boolean;
  sendGrantOfferedEmailsToEmployees: boolean;
  sendGrantWithdrawEmailsToEmployees: boolean;
  sendGrantReminderEmailsToEmployees: boolean;
  sendVestingCompleteEmailsToEmployees: boolean;
  sendBuyBackOfferedEmailsToEmployees: boolean;
  sendBuyBackClosedEmailsToEmployees: boolean;
  sendBuyBackReminderEmailsToEmployees: boolean;
  sendEmailsToAdmin: boolean;
  sendGrantEmailsToAdmin: boolean;
  sendPlanEmailsToAdmin: boolean;
  sendTransactionEmailsToAdmin: boolean;
  sendReminderEmailsToAdmin: boolean;
  employeeSignatureForGrant: boolean;
  requestApproverSignature: boolean;
  isNomineeMandatory?: boolean;
  showOptionsExpiryDateToEmployees: boolean;
}

export interface SignerDetails {
  id?: number;
  name: string;
  email: string;
  userId?: number;
  signerType?: SignerType;
  toBeAdded?: boolean;
  toBeUpdated?: boolean;
  toBeDeleted?: boolean;
}

export type SignerType = "OPTIONS";

export interface TemplateContent {
  defaultEmailBodyFile?: string;
  emailBodyFile?: string;
  emailSubject?: string;
  emailTemplateName?: string;
  emailTemplateType?: string;
}

export interface CompanyProfileDetails {
  id: string;
  name: string;
  cin: string;
  companyCurrency: string;
  dateOfIncorporation: string;
  address: string;
  email: string;
  companyLogoPath: string;
  operatingAs: string;
  nic: string;
  typeOfTheCompany?: string;
  stockSymbol?: string;
  companyLogoDisplaySettings?: {
    orientation: "portrait" | "landscape";
    displayFormat: "logoOnly" | "logoAndName" | "nameOnly";
  };
}

export type EmployeePortalSetting = {
  isNomineeMandatory: boolean;
  showPPSToEmployees: boolean;
  showAtGrantPPSToEmployees: boolean;
  showEmpPortalValuesForecast: boolean;
  showPlanToEmployees: boolean;
  mfaMandatoryForEmployee: boolean;
  enableGrantLetterDownload: boolean;
  allowEmployeeStatementDownload: boolean;
  showEquivalentShares: boolean;
  showFractionalPartOfEquivalentShares: boolean;
  showPlanConversionRatio: boolean;
  authMethods: string[];
  generateCredentialsViaLink: boolean;
  allowEmployeesToDownloadPlanDocuments: boolean;
  showTransactionsWhenEmpty: boolean;
  hideExerciseValueForEmployees: boolean;
  showRemainingOptionsCardOnDashboard: boolean;
  showOptionsCardOnDashboard: boolean;
  showRealizedCardOnDashboard: boolean;
  showUnrealizedCardOnDashboard: boolean;
  showLossCardOnDashboard: boolean;
  showPercentagesInEmployeePortalTransactions: boolean;
  showVestingTriggerType: boolean;
  showOptionsExpiryDateToEmployees: boolean;
  requireEmployeeNominationSignature: boolean;
  nomineeFormTemplates: NomineeFormTemplate[] | null;
};
export type EmailSetting = {
  communicationEmailId: string;
  senderNameForEmail: string;
  reminderEmailsToBeSent: boolean;
  reminderEmailsInterval: number;
  reminderEmailsStartFrom: number;
  isAttachmentEnabled: boolean;
  isAttachmentEnabledForAdmins: boolean;
  employeeInactivityLogout: boolean;
  approverMailToAdmin: boolean;
  emailsToBeSent: boolean;
  sendEmailsToEmployees: boolean;
  sendGrantEmailsToEmployees: boolean;
  sendVestingEmailsToEmployees: boolean;
  sendBuyBackEmailsToEmployees: boolean;
  sendGrantOfferedEmailsToEmployees: boolean;
  sendGrantWithdrawEmailsToEmployees: boolean;
  sendGrantReminderEmailsToEmployees: boolean;
  sendVestingCompleteEmailsToEmployees: boolean;
  sendBuyBackOfferedEmailsToEmployees: boolean;
  sendBuyBackClosedEmailsToEmployees: boolean;
  sendForfeitureLeterToEmployees: boolean;
  sendBuyBackReminderEmailsToEmployees: boolean;
  sendFullAndFinalLetterToEmployees: boolean;
  sendEmailsToAdmin: boolean;
  sendGrantEmailsToAdmin: boolean;
  sendPlanEmailsToAdmin: boolean;
  sendTransactionEmailsToAdmin: boolean;
  sendReminderEmailsToAdmin: boolean;
  // employeeSignatureForGrant: boolean;
  sendMonthlyStatement?: boolean;
  monthlyStatementInterval?: number;
  emailTemplateCC?: any;
  sendCCEmailsToAdmins?: boolean;
  sendResignationReminderEmailsToAdmin: boolean;
  sendOptionsExpiryReminderEmailsToEmployee: boolean;
  optionsExpiryReminderStartFromEmployee: number;
  optionsExpiryReminderIntervalEmployee: number;
  sendEmployeeDataSyncRecords: boolean;
  sendGrantAcceptedEmailsToEmployees: boolean;
  sendGrantAcceptedEmailsToAdmin: boolean;
  sendZeroVestingEmailsToEmployees?: boolean;
  sendPendingResignationSummaryEmailsToAdmin: boolean;
  pendingResignationInclusionOffset: number;
  pendingResignationReminderInterval: number;
  sendOptionsExpiryReminderEmailsToAdmin: boolean;
  optionsExpiryReminderStartFromAdmin: number;
  optionsExpiryReminderIntervalAdmin: number;
  sendMilestoneCompletionReminderEmailsToAdmin: boolean;
};

export type GrantLetterSetting = {
  acceptanceNumber: number;
  employeeSignatureForGrant: boolean;
  requestApproverSignature: boolean;
  overrideGrantLetter: boolean;
  enableAutoExerciseRequest: boolean;
  grantLetterTemplates: GrantLetterTemplate[];
  authorizedSignature: string | undefined;
  dateFormat?: string;
  dateForLapsing?: string;
  autoPauseVestingOnResignation?: boolean;
  discardOfferedGrantsOnResignation?: boolean;
  generatePlanDocument?: boolean;
  showZeroVestingInGrantLetter?: boolean;
  showValueAtGrantToAdmin?: boolean;
  excludeWithdrawnFromTotal?: boolean;
  disableAutoGrantLetterGeneration?: boolean;
};

export type ESignSetting = {
  isEsignEnabled: boolean;
  isDocuSignEnabled: boolean;
  signerDetails: SignerDetails[];
  signerDetailsDtos?: SignerDetails[];
  isDigioEnabled: boolean;
  useDigioElectronic?: boolean;
  useDigioAadhar?: boolean;
};

export type ListOfEmailTemplates = {
  companyId?: string;
  id: string;
  templateName: string;
  templateType: string;
  name: string;
  description: string;
  subject: string;
  overriddenByCompany: boolean;
  content?: string;
  isEdited?: boolean;
};

export interface EmailTemplate {
  content?: string;
  footer?: string;
  header?: string;
  subject?: string;
}

export interface EmailTemplateSaveObject {
  emailTemplateBody?: string;
  templateId: string;
  isEdited?: boolean;
  subject?: string;
  emailTemplateBodyBase64?: string;
}
export type EmailCCSettings = {
  sendCCEmailsToAdmins: boolean;
  emailTemplateCC?: { [k: string]: string[] };
};

export const AuthMethods: { [key: string]: string } = {
  "Password Based": "passwordBased",
  "Sign in with Google": "google",
  "Sign in with Microsoft": "microsoft",
};
